// src/components/copiloto/CopilotoCompromisso.js — o card de proposta na agenda.
//
// Out/26 (fase 3 da Agenda). O Copiloto só PROPÕE marcar, remarcar ou
// cancelar; este card é onde a pessoa confere e confirma — o mesmo clique
// que existe nos Detalhes da agenda, na proposta tracejada. O servidor refaz
// todas as conferências no clique (services/copiloto_agenda):
//   - se apareceu um compromisso no mesmo horário depois do card, ele NÃO
//     grava e devolve o card atualizado com o conflito — a pessoa confirma
//     o que vê;
//   - se o compromisso foi mexido por outra pessoa depois da proposta, ele
//     recusa e pede pra pedir de novo;
//   - clique duplo devolve o mesmo compromisso.
//
// Fiel ao card do AgendaTela.dc.html: Contato / Quando / Responsável /
// Lembrete ao cliente em grade, a citação da conversa ("Na conversa") e o
// rodapé "Nada é marcado nem enviado ao cliente antes da sua confirmação".
// As cores vêm do tema do Copiloto (tema.js): escuro no dashboard, o tema
// da Agenda dentro dela.
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiCheck } from 'react-icons/fi';
import api from '../../services/Api';
import { ACENTO, C, FONT_TXT, T } from './tema';

const SELOS = {
  pending: ['Aguardando você', T.ambar, T.ambarBg, T.ambarB],
  confirming: ['Gravando', T.ambar, T.ambarBg, T.ambarB],
  executed: ['Confirmado', C.green, T.verdeBg, T.verdeB('rgba(0,214,124,0.28)')],
  cancelled: ['Descartado', C.text2, T.neutroBg('rgba(255,255,255,0.04)'), T.neutroB],
  expired: ['Expirado', C.text2, T.neutroBg('rgba(255,255,255,0.04)'), T.neutroB],
  failed: ['Não gravado', C.red, T.vermelhoBg('rgba(255,69,96,0.10)'), T.vermelhoB('rgba(255,69,96,0.28)')],
};

const focoVisivel = { '&:focus-visible': { outline: `2px solid ${ACENTO.base}`, outlineOffset: 2 } };

const botaoSecundario = {
  fontFamily: FONT_TXT, fontSize: '11.2px', fontWeight: 600, color: C.text2, bgcolor: 'transparent',
  border: `1px solid ${C.border}`, borderRadius: '6px', p: '6.4px 12px', cursor: 'pointer',
  transition: 'all .15s', '&:hover': { color: C.text, bgcolor: T.hover },
  '&:disabled': { opacity: 0.5, cursor: 'default' }, '@media (pointer: coarse)': { minHeight: 40 }, ...focoVisivel,
};

const botaoPrincipal = {
  display: 'flex', alignItems: 'center', gap: '7px', fontFamily: FONT_TXT, fontSize: '11.2px',
  fontWeight: 700, color: T.sobreVerde, bgcolor: C.green, border: 'none', borderRadius: '6px',
  p: '7.4px 13px', cursor: 'pointer', letterSpacing: '0.02em', transition: 'background .15s',
  '&:hover': { bgcolor: T.verdeHover }, '&:disabled': { cursor: 'default', opacity: 0.85 },
  '@media (pointer: coarse)': { minHeight: 40 }, ...focoVisivel,
};

const linha = `1px solid ${T.linha('rgba(255,255,255,0.06)')}`;

function Faixa({ cor, fundo, borda, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', borderTop: `1px solid ${borda}`, background: `linear-gradient(90deg, ${fundo} 0%, transparent 58%)`, animation: 'cpIn .25s ease' }}>
      <Box sx={{ width: '2px', bgcolor: cor, flexShrink: 0 }} />
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, p: '10px 14px', flexWrap: 'wrap' }}>{children}</Box>
    </Box>
  );
}

export default function CopilotoCompromisso({ acao: inicial, aoMudar, aoVer }) {
  const navigate = useNavigate();
  const [acao, setAcao] = useState(inicial);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState('');
  // A agenda relê a sessão quando a proposta é confirmada lá nos Detalhes:
  // o card chega com o estado novo e precisa mostrar.
  useEffect(() => { setAcao(inicial); }, [inicial]);

  const status = ocupado ? 'confirming' : acao.status;
  const [rotulo, cor, fundo, borda] = SELOS[status] || SELOS.pending;
  const pendente = acao.status === 'pending';
  const cancelar = acao.operacao === 'cancelar';

  const atualizar = (nova, extra) => { setAcao(nova); if (aoMudar) aoMudar(nova, extra); };

  const confirmar = async () => {
    if (ocupado) return;
    setOcupado(true);
    setAviso('');
    try {
      const { data } = await api.post(`/copilot/actions/${acao.acao_id}/confirm`, { contatos_vistos: 0 });
      atualizar(data.acao, data.evento);
    } catch (e) {
      const d = e?.response?.data;
      if (d?.acao) atualizar(d.acao);
      setAviso(d?.detail || (e?.response ? 'Não foi possível gravar.' : 'Sem conexão com o servidor. Nada foi gravado.'));
    } finally {
      setOcupado(false);
    }
  };

  const descartar = async () => {
    if (ocupado) return;
    setOcupado(true);
    try {
      const { data } = await api.post(`/copilot/actions/${acao.acao_id}/cancel`);
      atualizar(data.acao);
      setAviso('');
    } catch (e) {
      const d = e?.response?.data;
      if (d?.acao) atualizar(d.acao);
      setAviso(d?.detail || 'Não foi possível descartar.');
    } finally {
      setOcupado(false);
    }
  };

  const ver = () => {
    const ev = acao.evento || {};
    const id = acao.resultado?.evento_id;
    if (aoVer) { aoVer({ id, data: acao.resultado?.data || ev.data, ini: acao.resultado?.ini || ev.ini }); return; }
    const busca = new URLSearchParams({ ...(id ? { evento: id } : {}), data: acao.resultado?.data || ev.data });
    navigate(`/agenda?${busca.toString()}`);
  };

  const citacao = acao.citacao;
  return (
    <Box
      role="group"
      aria-label={acao.titulo}
      data-acao={acao.acao_id}
      sx={{
        maxWidth: 760, bgcolor: C.bg, border: `1px solid ${T.cardBorda}`, borderRadius: '10px',
        overflow: 'hidden', opacity: ['cancelled', 'expired'].includes(acao.status) ? 0.6 : 1, transition: 'opacity .2s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '11px 14px', borderBottom: linha }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: '7px', bgcolor: T.verdeBg, border: `1px solid ${T.verdeB('rgba(0,214,124,0.22)')}`,
          color: C.green, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <FiCalendar size={14} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: '13.12px', fontWeight: 700, color: C.text }}>{acao.titulo}</Box>
          <Box sx={{ fontSize: '10.56px', color: C.text2 }}>Revise os dados que o Copiloto entendeu</Box>
        </Box>
        <Box component="span" sx={{
          fontSize: '9.28px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', p: '3px 8px',
          borderRadius: '20px', color: cor, bgcolor: fundo, border: `1px solid ${borda}`, whiteSpace: 'nowrap', flexShrink: 0,
        }}>{rotulo}</Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: `repeat(${Math.min(4, (acao.linhas || []).length || 1)}, minmax(0, 1fr))` }, gap: '10px 12px', p: '9px 14px' }}>
        {(acao.linhas || []).map((l) => (
          <Box key={l.k} sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <Box component="span" sx={{ fontSize: '9.92px', color: C.text3, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{l.k}</Box>
            <Box component="span" sx={{ fontSize: '12.48px', fontWeight: 600, color: C.text, overflowWrap: 'anywhere' }}>{l.v}</Box>
            {l.sub && <Box component="span" sx={{ fontSize: '10.56px', color: C.text2, lineHeight: 1.4, overflowWrap: 'anywhere' }}>{l.sub}</Box>}
          </Box>
        ))}
      </Box>

      {citacao && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px 8px', m: '0 14px 10px', p: '7px 10px', borderRadius: '7px', bgcolor: C.bg3 }}>
          <Box component="span" sx={{ fontSize: '9.92px', color: C.text3, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', flexShrink: 0 }}>Na conversa</Box>
          <Box component="span" sx={{ fontSize: '11.52px', color: C.text, flex: '1 1 200px', minWidth: 0, overflowWrap: 'anywhere' }}>“{citacao.texto}”</Box>
          <Box component="span" sx={{ fontSize: '10.56px', color: C.text2, ml: 'auto', whiteSpace: 'nowrap' }}>{citacao.autor} · {citacao.quando}</Box>
        </Box>
      )}

      {(acao.avisos || []).length > 0 && pendente && (
        <Box sx={{ px: '14px', pb: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {acao.avisos.map((a) => (
            <Box key={a} sx={{ display: 'flex', gap: '6px', fontSize: '10.88px', color: T.ambar, lineHeight: 1.45 }}>
              <Box component="span" aria-hidden="true">•</Box>{a}
            </Box>
          ))}
        </Box>
      )}

      {pendente && aviso && (
        <Box role="alert" sx={{ px: '14px', pb: '10px', fontSize: '10.88px', color: T.ambar, lineHeight: 1.45 }}>{aviso}</Box>
      )}

      {pendente && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '10px 14px', borderTop: linha, bgcolor: T.rodape, flexWrap: 'wrap' }}>
          <Box component="span" sx={{ flex: { xs: '1 1 100%', sm: 1 }, fontSize: '10.56px', color: C.text2 }}>
            {cancelar ? 'Nada é cancelado antes da sua confirmação.' : 'Nada é marcado nem enviado ao cliente antes da sua confirmação.'}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Box component="button" type="button" onClick={descartar} disabled={ocupado} sx={botaoSecundario}>Descartar</Box>
            <Box component="button" type="button" onClick={confirmar} disabled={ocupado} sx={botaoPrincipal}>
              {ocupado ? 'Gravando…' : acao.rotulo_botao}
            </Box>
          </Box>
        </Box>
      )}

      {acao.status === 'executed' && (
        <Faixa cor={C.green} fundo={T.verdeBg} borda={T.verdeB('rgba(0,214,124,0.2)')}>
          <FiCheck size={14} color={C.green} />
          <Box component="span" sx={{ flex: 1, minWidth: 160, fontSize: '11.52px', color: C.text }}>
            {acao.resultado?.ok_texto || 'Gravado na agenda.'}
          </Box>
          {!cancelar && (
            <Box component="button" type="button" onClick={ver}
              sx={{ fontFamily: FONT_TXT, fontSize: '11.2px', fontWeight: 700, color: C.green, bgcolor: 'transparent', border: 'none', p: 0, cursor: 'pointer', whiteSpace: 'nowrap', ...focoVisivel }}>
              Ver na agenda →
            </Box>
          )}
        </Faixa>
      )}

      {acao.status === 'cancelled' && (
        <Box sx={{ p: '10px 14px', borderTop: linha, fontSize: '11.2px', color: C.text2 }}>
          {acao.erro ? `${acao.erro} Nada foi gravado.` : (cancelar ? 'Descartado. O compromisso continua na agenda.' : 'Descartado. Nada foi marcado.')}
        </Box>
      )}

      {acao.status === 'expired' && (
        <Box sx={{ p: '10px 14px', borderTop: linha, fontSize: '11.2px', color: C.text2 }}>
          {cancelar ? 'Este card expirou sem confirmação.' : 'O horário proposto já passou.'} Peça de novo ao Copiloto se ainda precisar.
        </Box>
      )}

      {acao.status === 'failed' && (
        <Faixa cor={C.red} fundo={T.vermelhoBg('rgba(255,69,96,0.10)')} borda={T.vermelhoB('rgba(255,69,96,0.22)')}>
          <Box component="span" role="alert" sx={{ flex: 1, fontSize: '11.2px', color: C.text, lineHeight: 1.5 }}>
            {acao.erro || 'Não foi possível gravar.'} Nada mudou na agenda.
          </Box>
        </Faixa>
      )}
    </Box>
  );
}
